/* Mission Control: Home hero effects (Theme settings, Effects). Both are visual layers only; the page's text never changes.
   Star numbers: when the results come into view, each figure assembles from drifting stars and settles into the crisp
   number, once per page view, staggered across the strip. The stars take the sky's light (the time of day palette) and
   warm inside the lantern; the lit figures glow in Dj's lime. The figures stay in the page as text throughout; only
   their ink waits for the stars.
   Content to Code: as the visitor starts to scroll, the first emphasised word ("Content") dissolves into terminal
   characters in the Code accent and rebuilds, and a cursor carries the signal on to the last word ("Code"). Scrolling
   back to the top plays it in reverse. The moving copy sits outside the heading and exists only while it plays.
   Reduced motion and the theme editor: neither runs (the numbers simply appear). */
(() => {
  const root = document.documentElement;
  const fx = (root.getAttribute('data-fx') || '').split(' ');
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || root.classList.contains('shopify-design-mode')) return;
  const hero = document.querySelector('[data-mc-hero]');
  if (!hero) return;
  const rgb = (name, fb) => {
    const v = getComputedStyle(root).getPropertyValue(name).trim();
    const a = v ? v.split(',').map((n) => parseFloat(n)) : null;
    return a && a.length === 3 && a.every((n) => n >= 0) ? a : fb;
  };
  const mix = (a, b, t) => a.map((x, i) => Math.round(x + (b[i] - x) * t));

  /* ---------- Star numbers ---------- */
  function starNumbers() {
    const nums = [...hero.querySelectorAll('[data-mc-starnum]')];
    const wrap = hero.querySelector('.mc-hero__telemetry-wrap');
    // the figures wait from first paint (CSS, see mc-hero.liquid); from here the script owns when they light
    nums.forEach((n) => n.classList.add('is-starwait'));
    const light = (el) => {
      if (!el.hasAttribute('data-mc-starnum')) return;
      el.classList.add('is-starlit');
      el.classList.remove('is-starwait');
      el.removeAttribute('data-mc-starnum');
      setTimeout(() => el.classList.remove('is-starlit'), 800); // the glow then follows the scroll again, untimed
    };
    if (!nums.length || !wrap || !('IntersectionObserver' in window)) { nums.forEach(light); return; }
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const canvas = document.createElement('canvas');
    canvas.className = 'mc-starnums';
    canvas.setAttribute('aria-hidden', 'true');
    let ctx = null, parts = [], groups = [], t0 = 0, raf = 0, done = false, started = false, inView = false, padX = 0, padT = 0;
    const vw0 = window.innerWidth;
    // Unhurried: each figure gathers over about 2.2s (stars wait up to 0.5s, then fly 1.3 to 1.7s); the strip of six
    // finishes in about 3.3s
    const STAGGER = 160, MAXD = 500, FLIGHT = 1700, FADE = 300;

    const finish = () => {
      if (done) return;
      done = true;
      cancelAnimationFrame(raf);
      nums.forEach(light);
      canvas.remove();
      io.disconnect();
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('resize', onResize);
    };
    // phones resize as the address bar slides; only a real width change ends the effect
    const onResize = () => { if (window.innerWidth !== vw0) finish(); };
    const onHide = () => { if (document.hidden) finish(); };

    // Where each figure's ink sits: per character, from the live layout (the strip may be mid-entrance or scaled by the
    // pinned scene, so positions are measured in the strip's own, untransformed space)
    function build() {
      const wr = wrap.getBoundingClientRect();
      const k = wr.width / (wrap.offsetWidth || 1) || 1;
      padX = 24;
      padT = Math.round(Math.min(170, window.innerHeight * 0.2));
      const W = wrap.offsetWidth + padX * 2, H = wrap.offsetHeight + padT + 24;
      Object.assign(canvas.style, { left: `${-padX}px`, top: `${-padT}px`, width: `${W}px`, height: `${H}px` });
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      wrap.appendChild(canvas);
      ctx = canvas.getContext('2d');
      const mask = document.createElement('canvas');
      const mx = mask.getContext('2d', { willReadFrequently: true });
      const sky = [rgb('--mc-sky-v', [160, 132, 255]), rgb('--mc-sky-t', [124, 200, 255]), rgb('--mc-sky-b', [124, 200, 255])];
      const wide = window.innerWidth >= 750;
      nums.forEach((el, j) => {
        const node = el.firstChild;
        if (!node || node.nodeType !== 3 || !node.nodeValue.trim()) return;
        const cs = getComputedStyle(el);
        const fs = parseFloat(cs.fontSize);
        const er = el.getBoundingClientRect();
        // the baseline, exactly: a zero-size inline mark sits on it
        const mark = document.createElement('span');
        mark.style.cssText = 'display:inline-block;width:0;height:0;vertical-align:baseline';
        el.appendChild(mark);
        const base = mark.getBoundingClientRect().top - er.top;
        mark.remove();
        let ox = 0, oy = 0;
        for (let n = el; n && n !== wrap; n = n.offsetParent) { ox += n.offsetLeft; oy += n.offsetTop; }
        const range = document.createRange();
        const text = node.nodeValue;
        const chars = [];
        for (let i = 0; i < text.length; i++) {
          if (!text[i].trim()) continue;
          range.setStart(node, i);
          range.setEnd(node, i + 1);
          const r = range.getBoundingClientRect();
          chars.push({ ch: text[i], x: (r.left - er.left) / k, w: r.width / k });
        }
        if (!chars.length) return;
        const x0 = Math.floor(chars[0].x) - 2, x1 = Math.ceil(chars[chars.length - 1].x + chars[chars.length - 1].w) + 2;
        const bw = Math.max(4, x1 - x0), bh = Math.ceil(fs * 1.4);
        const by = base / k - fs * 1.05;
        mask.width = bw;
        mask.height = bh;
        mx.clearRect(0, 0, bw, bh);
        mx.fillStyle = '#fff';
        mx.textBaseline = 'alphabetic';
        mx.font = `${cs.fontWeight} ${fs}px ${cs.fontFamily}`;
        chars.forEach((c) => {
          const m = mx.measureText(c.ch).width || c.w;
          mx.save();
          mx.translate(c.x - x0, fs * 1.05);
          mx.scale(c.w / m, 1); // match the face's width axis as the page sets it
          mx.fillText(c.ch, 0, 0);
          mx.restore();
        });
        const data = mx.getImageData(0, 0, bw, bh).data;
        const pts = [];
        for (let y = 0; y < bh; y++) for (let x = 0; x < bw; x++) if (data[(y * bw + x) * 4 + 3] > 150) pts.push(x, y);
        const count = pts.length / 2;
        const want = Math.min(count, Math.round(Math.max(110, Math.min(250, fs * 6.2))));
        const tone = sky[j % 3];
        const R = Math.min(wide ? 150 : 100, fs * (wide ? 3.4 : 3));
        const dir = j % 2 ? 1 : -1;
        const tones3 = [0.32, 0.52, 0.72].map((m) => tint(mix([233, 246, 255], tone, m)));
        const g = { el, t: j * STAGGER, lit: false };
        groups.push(g);
        for (let n = 0; n < want; n++) {
          const pi = Math.floor(Math.random() * count) * 2;
          const tx = padX + ox + x0 + pts[pi] + Math.random() - 0.5;
          const ty = padT + oy + by + pts[pi + 1] + Math.random() - 0.5;
          // stars arrive from the sky above, swirling in on a curve
          const a = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.15;
          const d = R * (0.45 + Math.random() * 0.75);
          // phones stack the results in rows: their stars stay close to their own row, so no label is crossed
          const sx = tx + Math.cos(a) * d + (Math.random() - 0.5) * fs, sy = ty + Math.sin(a) * d * (wide ? 1 : 0.55);
          parts.push({
            g, tx, ty, sx, sy,
            cx: (sx + tx) / 2 - (ty - sy) * 0.32 * dir,
            cy: (sy + ty) / 2 + (tx - sx) * 0.32 * dir,
            d: Math.random() * MAXD,
            f: FLIGHT * (0.78 + Math.random() * 0.22),
            c: tones3[n % 3],
            s: (wide ? 1.5 : 1.25) * (Math.random() < 0.08 ? 1.7 : 1),
            ph: Math.random() * 6.28,
            px: sx, py: sy,
          });
        }
      });
    }

    // Colours are bucketed (tone x landed x lantern warmth x alpha), so each frame sets a fill only a few dozen times
    const WARM = [255, 196, 120], INK = [246, 249, 255];
    const tones = [];
    function tint(c) { tones.push(c, mix(c, INK, 0.7)); return (tones.length - 2) / 2; }
    const styles = new Map();
    const styleOf = (key) => {
      let st = styles.get(key);
      if (!st) {
        const w = (key >> 1) & 3, base = tones[(key >> 3) * 2 + (key & 1)];
        const c = w ? mix(base, WARM, w / 3 * 0.85) : base;
        st = `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
        styles.set(key, st);
      }
      return st;
    };
    const buckets = new Map();
    function frame(now) {
      raf = 0;
      if (done) return;
      const t = now - t0;
      const W = canvas.width, H = canvas.height;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, W, H);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.globalCompositeOperation = 'lighter';
      // the lantern, in the strip's own coordinates
      const L = window.McLantern;
      let lx = 0, ly = 0, lr = 0, ls = 0;
      if (L && L.s > 0.02 && L.r) {
        const wr = wrap.getBoundingClientRect();
        const k = wr.width / (wrap.offsetWidth || 1) || 1;
        lx = (L.x - wr.left) / k + padX; ly = (L.y - wr.top) / k + padT; lr = L.r / k; ls = L.s;
      }
      buckets.clear();
      let alive = 0;
      for (const p of parts) {
        const g = p.g;
        const lt = t - g.t;
        const end = MAXD + FLIGHT;
        if (lt > end + FADE) continue;
        alive++;
        let u = (lt - p.d) / p.f;
        u = u < 0 ? 0 : u > 1 ? 1 : u;
        const e = 1 - Math.pow(1 - u, 3);
        const iu = 1 - e;
        // before launch: a faint star drifting where it waits
        const drift = (1 - u) * Math.sin(now / 900 + p.ph) * 3;
        const x = iu * iu * p.sx + 2 * iu * e * p.cx + e * e * p.tx + drift;
        const y = iu * iu * p.sy + 2 * iu * e * p.cy + e * e * p.ty;
        let a = Math.min(1, (t + 120) / 260) * (0.45 + 0.55 * e);
        if (lt > end) a *= 1 - (lt - end) / FADE;
        if (u > 0 && u < 1) a = Math.min(1, a + 0.25);
        let w = 0;
        if (ls) {
          const dd = Math.hypot(x - lx, y - ly) / lr;
          if (dd < 1) w = Math.min(3, Math.round((1 - dd) * (1 - dd) * ls * 3.4));
        }
        const ai = Math.round(a * 6);
        const ck = (p.c << 3) | (w << 1) | (u >= 1 ? 1 : 0);
        const key = ck * 8 + ai;
        let b = buckets.get(key);
        if (!b) { b = { ck, a: ai / 6, pts: [] }; buckets.set(key, b); }
        const moving = u > 0 && u < 1;
        b.pts.push(x, y, moving ? p.s : p.s * 0.85, moving ? x - p.px : 0, moving ? y - p.py : 0);
        p.px = x; p.py = y;
      }
      buckets.forEach((b) => {
        if (b.a <= 0) return;
        ctx.globalAlpha = b.a;
        ctx.fillStyle = ctx.strokeStyle = styleOf(b.ck);
        ctx.beginPath();
        const P = b.pts;
        for (let i = 0; i < P.length; i += 5) ctx.rect(P[i] - P[i + 2] / 2, P[i + 1] - P[i + 2] / 2, P[i + 2], P[i + 2]);
        ctx.fill();
        // in flight, each star leaves a short, fainter streak behind it
        ctx.globalAlpha = b.a * 0.55;
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        let any = false;
        for (let i = 0; i < P.length; i += 5) {
          const vx = P[i + 3], vy = P[i + 4];
          if (vx * vx + vy * vy < 4) continue;
          any = true;
          ctx.moveTo(P[i], P[i + 1]);
          ctx.lineTo(P[i] - vx, P[i + 1] - vy);
        }
        if (any) ctx.stroke();
      });
      // each figure's crisp ink lights just as its stars land
      groups.forEach((g) => { if (!g.lit && t - g.t > MAXD + FLIGHT - 140) { g.lit = true; light(g.el); } });
      if (!alive) { finish(); return; }
      raf = requestAnimationFrame(frame);
    }

    function start() {
      if (started || done || !inView) return;
      started = true;
      if (document.hidden) { finish(); return; }
      try { build(); } catch (e) { finish(); return; }
      if (!parts.length) { finish(); return; }
      t0 = performance.now();
      document.addEventListener('visibilitychange', onHide);
      window.addEventListener('resize', onResize);
      raf = requestAnimationFrame(frame);
      // never leave a figure waiting, whatever happens to the frame loop
      setTimeout(finish, STAGGER * nums.length + MAXD + FLIGHT + FADE + 1500);
    }

    // Plays when the strip is well in view (after the hero's entrance, with the brand face ready); if it leaves the
    // screen or the tab is hidden mid-flight, the figures simply finish
    const ready = Promise.race([document.fonts ? document.fonts.ready : Promise.resolve(), new Promise((r) => setTimeout(r, 1500))]);
    const io = new IntersectionObserver((entries) => {
      const e = entries[entries.length - 1];
      inView = e.isIntersecting;
      if (started) { if (!e.isIntersecting) finish(); return; }
      if (e.intersectionRatio < 0.4) return;
      ready.then(() => setTimeout(start, Math.max(0, 750 - performance.now())));
    }, { threshold: [0, 0.4, 0.7] });
    io.observe(wrap.querySelector('.mc-hero__telemetry') || wrap);
  }

  /* ---------- Content to Code ---------- */
  function contentToCode() {
    const content = hero.querySelector('.mc-hero__content');
    const words = [...hero.querySelectorAll('.mc-hero__em .mc-hero__word')];
    if (!content || !words.length) return;
    const word = words[0], last = words[words.length - 1];
    const GLYPHS = '{}<>/;=_#$*+[]01:'.split('');
    const STEP = 48, OUT = 70, CYCLE = 60, LIFE = 270;
    let coded = false, busy = false, ticking = false, waiting = false;

    function play(forward) {
      busy = true;
      const node = word.firstChild;
      if (!node || node.nodeType !== 3) { busy = false; return; }
      const text = node.nodeValue;
      const cr = content.getBoundingClientRect();
      const k = cr.width / (content.offsetWidth || 1) || 1;
      const wr = word.getBoundingClientRect();
      const cs = getComputedStyle(word);
      const ov = document.createElement('span');
      ov.className = 'mc-morph';
      ov.setAttribute('aria-hidden', 'true');
      Object.assign(ov.style, {
        left: `${(wr.left - cr.left) / k}px`, top: `${(wr.top - cr.top) / k}px`, height: `${wr.height / k}px`,
        fontFamily: cs.fontFamily, fontSize: cs.fontSize, fontWeight: cs.fontWeight, fontStretch: cs.fontStretch,
        letterSpacing: cs.letterSpacing, lineHeight: cs.lineHeight,
      });
      const range = document.createRange();
      const cells = [];
      for (let i = 0; i < text.length; i++) {
        range.setStart(node, i);
        range.setEnd(node, i + 1);
        const s = document.createElement('span');
        s.className = 'mc-morph__ch';
        s.textContent = text[i];
        s.style.width = `${range.getBoundingClientRect().width / k}px`;
        ov.appendChild(s);
        cells.push(s);
      }
      const cursor = document.createElement('span');
      cursor.className = 'mc-morph__cursor';
      ov.appendChild(cursor);
      content.appendChild(ov);
      word.classList.add('is-morphing');
      // the cursor's two stops on the last word: its end (forward) or where it begins (reverse)
      const lr = last.getBoundingClientRect();
      const endX = (lr.right - wr.left) / k + 4;
      const order = cells.map((_, i) => (forward ? i : cells.length - 1 - i));
      const offs = cells.map((c) => c.offsetLeft), widths = cells.map((c) => c.offsetWidth), cw = cursor.offsetWidth;
      const n = cells.length;
      const waveEnd = (n - 1) * STEP + LIFE;
      const t0 = performance.now();
      let rafId = 0;
      const cleanup = () => {
        cancelAnimationFrame(rafId);
        ov.remove();
        word.classList.remove('is-morphing');
        last.classList.remove('is-coded');
        last.classList.add('is-uncoding');
        setTimeout(() => last.classList.remove('is-uncoding'), 700);
        document.removeEventListener('visibilitychange', onHide);
        busy = false;
        check();
      };
      const onHide = () => { if (document.hidden) cleanup(); };
      document.addEventListener('visibilitychange', onHide);
      const tick = (now) => {
        const t = now - t0;
        let front = -1;
        order.forEach((i, q) => {
          const lt = t - q * STEP;
          const c = cells[i];
          let state;
          if (lt < 0) state = '';
          else if (lt < OUT) state = 'is-out';
          else if (lt < LIFE) state = 'is-glyph';
          else state = lt < LIFE + 40 ? 'is-in' : '';
          if (state === 'is-glyph') {
            const g = GLYPHS[(i * 7 + Math.floor(lt / CYCLE) * 5 + (forward ? 0 : 3)) % GLYPHS.length];
            if (c.textContent !== g) c.textContent = g;
            front = i;
          } else if (c.textContent !== text[i]) c.textContent = text[i];
          if (c.__s !== state) { c.className = `mc-morph__ch ${state}`.trim(); c.__s = state; }
        });
        // the cursor leads the wave, then carries the signal to the other word and blinks out
        if (front >= 0) {
          cursor.classList.add('is-on');
          // like a terminal's, the cursor sits on the next cell, the one about to change
          cursor.style.left = `${forward ? offs[front] + widths[front] + 2 : offs[front] - cw - 2}px`;
        } else if (t > waveEnd - 40 && t < waveEnd + 360) {
          cursor.style.left = forward ? `${endX}px` : `${-cw - 6}px`;
          cursor.classList.toggle('is-on', Math.floor((t - waveEnd) / 120) % 2 === 0);
          if (forward) last.classList.add('is-coded');
        } else cursor.classList.remove('is-on');
        if (!forward && t < 120) last.classList.add('is-coded');
        if (t < waveEnd + 440) rafId = requestAnimationFrame(tick);
        else cleanup();
      };
      rafId = requestAnimationFrame(tick);
    }

    // Plays when the visitor starts to scroll (never on load, and never before the headline has finished arriving);
    // back at the top it plays in reverse. Only while the headline is on screen.
    function check() {
      ticking = false;
      if (busy) return;
      const y = window.scrollY;
      const want = coded ? y > 8 : y > 24;
      if (want === coded) return;
      if (performance.now() < 1500) {
        if (!waiting) { waiting = true; setTimeout(() => { waiting = false; check(); }, 1500 - performance.now()); }
        return;
      }
      coded = want;
      const r = word.getBoundingClientRect();
      if (r.bottom < 0 || r.top > window.innerHeight || document.hidden) return;
      play(coded);
    }
    // a page restored mid-scroll starts in the scrolled state, with nothing to play
    coded = window.scrollY > 24;
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(check); } }, { passive: true });
  }

  if (fx.includes('stars')) starNumbers();
  if (fx.includes('glyph')) contentToCode();
})();
